// import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  isLoading,
  setAssetCorrelations,
  setAssetHolder,
  // setAssetsPeriodReturn,
  // setBullBearMean,
  setHighlights,
  setHistoricHolder,
  setHistory,
  setHoldings,
  setMaxDrawDown,
  setPortBench,
  // setStockReturns,
  setTopTraders,
  // setTransactions,
  setPnLData,
  setPairData,
  // setPortfolioScore
} from '../redux/portfolioReducer'
import { toast } from 'react-toastify';
import { store } from '../redux/store'
import axios from 'axios'
import { fetchEnvVar } from 'utils/envConfig';
import { validateAccessToken, validateWallets, validateTokenAddresses, validateChains } from 'utils/validation';
import { checkRateLimit } from 'utils/rateLimiter'; 

const apiUrl = process.env.REACT_APP_BASE_ENDPOINT

/////////////////////////
// CHECK WALLET VALIDITY
/////////////////////////

// POST / https://api.astrotek.app/api/is_valid_wallet
export async function checkWalletValidity(wallets: string[], accessToken: string) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  const sanitizedWallets = validateWallets(wallets, 10);
  
  try {
    const payload = { wallets: sanitizedWallets };
    const headers = { 
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const data = await axios.post(`${apiUrl}is_valid_wallet`, payload, { headers });
    return data;
  } catch (e) {
    store.dispatch(isLoading({ holdings: false }));
    console.error('Error checking wallet validity');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid wallet addresses provided')
      } else {
        toast.error('Unable to validate wallets. Please try again.')
      }
    } else {
      toast.error('Unable to validate wallets. Please try again.')
    }
    // Re-throw validation errors (already safe)
    return null;
  }
}





/////////////////////////
// GET TOKEN BANK DATA
/////////////////////////


// GET / sanitized version of getTokenBankData
export async function getTokenBankData(accessToken: string) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    const headers = { 
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const response = await axios.get(`${apiUrl}token_bank`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching token bank data');
    
    // Sanitize error for user
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        console.error('Authentication failed')
      } else {
        toast.error('Unable to fetch token data. Please try again.')
      }
    } else {
      toast.error('Unable to fetch token data. Please try again.')
    }
    return null;
  }
}





/////////////////////////
// GET SECURITY SCORES
/////////////////////////

// POST / https://api.astrotek.app/api/security_score
export async function getSecurityScores(token_addresses: string[], chains: string[], accessToken: string) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  const validAddresses = validateTokenAddresses(token_addresses);
  const validChains = validateChains(chains); 
  
  // Updated to handle mixed chains 
  try {
    const payload = { 
      token_addresses: validAddresses,
      chain: validChains
    };
    const headers = { 
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const response = await axios.post(`${apiUrl}security_score`, payload, { headers });
    return response.data;
  } catch (e) {
    console.error('Error fetching security scores');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid token addresses provided')
      } else {
        toast.error('Unable to fetch security scores. Please try again.')
      }
    } else {
      toast.error('Unable to fetch security scores. Please try again.')
    }
    return null;
  }
}






/////////////////////////
// GET TOKEN METADATA 
/////////////////////////

// POST / https://api.astrotek.app/api/token_metadata
export async function getTokenMetadata(token_addresses: string[], chains: string[], accessToken: string) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  const validAddresses = validateTokenAddresses(token_addresses);
  const validChains = validateChains(chains); 
  
  try {
    const payload = { 
      token_addresses: validAddresses, 
      chain: validChains
    };
    const headers = { 
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const response = await axios.post(`${apiUrl}token_metadata`, payload, { headers });
    return response.data;
  } catch (e) {
    console.error('Error fetching token metadata');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid token addresses provided')
      } else {
        toast.error('Unable to fetch token metadata. Please try again.')
      }
    } else {
      toast.error('Unable to fetch token metadata. Please try again.')
    }
    return null;
  }
}





/////////////////////////////////
// GET SINGLE TOKEN HOLDER COUNT 
/////////////////////////////////

// POST / https://api.astrotek.app/api/single_token_holder_count
export async function getSingleTokenHistoricalHolder(address: string, accessToken: string) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  const validAddresses = validateTokenAddresses(address);
  
  try {
    const payload = { 
      token_addresses: validAddresses
    };
    const headers = { 
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const response = await axios.post(`${apiUrl}single_token_holder_count`, payload, { headers });
    return response.data;
  } catch (e) {
    console.error('Error fetching holder history');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid token address provided')
      } else {
        toast.error('Unable to fetch holder history. Please try again.')
      }
    } else {
      toast.error('Unable to fetch holder history. Please try again.')
    }
    return null;
  }
}





/////////////////////////////////
// GET PORTFOLIO HIGHLIGHTS 
/////////////////////////////////

// POST / https://api.astrotek.app/api/portfolio_highlights
export async function getPortfolioHighlight(accessToken: string) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    const wallets = store.getState().user.wallets;
    const dataDuration = store.getState().user.dataDuration?.id;
    
    // Validate wallets from store
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('No wallets found in store')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    // Validate dataDuration
    const days = dataDuration || 7;
    if (typeof days !== 'number' || days <= 0) {
      console.error('Invalid data duration')
      return null;
    }
    
    store.dispatch(isLoading({ highlights: true }));
    
    const payload = {
      wallets: validWallets,
      days,
      rf: 0.01,
      benchmark: 'ETH',
    };
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const data = await axios.post(`${apiUrl}portfolio_highlights`, payload, { headers });
    store.dispatch(setHighlights(data.data));
    return data;
  } catch (e) {
    store.dispatch(isLoading({ highlights: false }));
    console.error('Error fetching portfolio highlights');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid portfolio data provided')
      } else {
        toast.error('Unable to fetch portfolio highlights. Please try again.')
      }
    } else {
      toast.error('Unable to fetch portfolio highlights. Please try again.')
    }
    return null;
  }
}






/////////////////////////////////
// GET HISTORIC PORTFOLIO 
/////////////////////////////////

// POST / https://api.astrotek.app/api/historic_portfolio
export async function getPortfolioHistory(accessToken: string) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    store.dispatch(isLoading({ history: true }));
    
    const wallets = store.getState().user.wallets;
    const dataDuration = store.getState().user.dataDuration?.id;
    
    // Validate wallets from store
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('No wallets found in store')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    // Validate dataDuration
    const days = dataDuration || 14;
    if (typeof days !== 'number' || days <= 0) {
      console.error('Invalid data duration')
      return null;
    }
    
    const payload = {
      wallets: validWallets,
      days,
    };
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const data = await axios.post(`${apiUrl}historic_portfolio`, payload, { headers });
    store.dispatch(setHistory(data.data));
    return data;
  } catch (e) {
    store.dispatch(isLoading({ history: false }));
    console.error('Error fetching portfolio history');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid portfolio data provided')
      } else {
        toast.error('Unable to fetch portfolio history. Please try again.')
      }
    } else {
        toast.error('Unable to fetch portfolio history. Please try again.')
    }
    return null;
  }
}







/////////////////////////////////
// GET PORTFOLIO 
/////////////////////////////////

// POST / https://api.astrotek.app/api/portfolio
export async function getPortfolio(accessToken: string) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    store.dispatch(isLoading({ holdings: true }));
    
    const wallets = store.getState().user.wallets;
    
    // Validate wallets from store
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('No wallets found in store')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    const payload = { wallets: validWallets };
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const data = await axios.post(`${apiUrl}portfolio`, payload, { headers });
    store.dispatch(setHoldings(data.data));
    return data;
  } catch (e) {
    store.dispatch(isLoading({ holdings: false }));
    console.error('Error retrieving portfolio data');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid wallet data provided')
      } else {
        toast.error('Unable to fetch portfolio data. Please try again.')
      }
    } else {
      toast.error('Unable to fetch portfolio data. Please try again.')
    }
    return null;
  }
}






/////////////////////////////////
// GET ASSET HOLDER 
/////////////////////////////////

// POST / Get asset holders
export async function getAssetHolder(accessToken: string, signal?: AbortSignal) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    store.dispatch(isLoading({ assetHolder: true }));
    
    const wallets = store.getState().user.wallets;
    const dataDuration = store.getState().user.dataDuration?.id;
    
    // Validate wallets from store
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('No wallets found in store')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    // Validate dataDuration
    const days = dataDuration || 14;
    if (typeof days !== 'number' || days <= 0) {
      console.error('Invalid data duration')
      return null;
    }
    
    const payload = {
      wallets: validWallets,
      days,
      rf: 0.01,
      benchmark: 'ETH',
    };
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const { data } = await axios.post(`${apiUrl}asset_holder_data`, payload, {
      headers,
      signal
    });
    
    // Process each contract's data
    const processedData = Object.entries(data).reduce((acc, [contractAddress, contractData]: [string, any]) => {
      acc[contractAddress.toLowerCase()] = {
        ...contractData,
        raw_holders: contractData.raw_holders || {},
        contract_address: contractAddress.toLowerCase()
      };
      return acc;
    }, {});
    
    store.dispatch(setAssetHolder(processedData));
    return processedData;
  } catch (e) {
    if (e.name === 'AbortError') return;
    store.dispatch(isLoading({ assetHolder: false }));
    console.error('Error retrieving asset holder information');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid asset holder data provided')
      } else {
        toast.error('Unable to fetch asset holder data. Please try again.')
      }
    } else {
      toast.error('Unable to fetch asset holder data. Please try again.')
    }
    return null;
  }
}









/////////////////////////////////
// GET TOP TRADERS 
/////////////////////////////////

// POST / Get top traders
export async function getTopTraders(accessToken: string, signal?: AbortSignal) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    store.dispatch(isLoading({ topTraders: true }));
    
    const wallets = store.getState().user.wallets;
    const dataDuration = store.getState().user.dataDuration?.id;
    
    // Validate wallets from store
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('No wallets found in store')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    // Validate dataDuration
    const days = dataDuration || 14;
    if (typeof days !== 'number' || days <= 0) {
      console.error('Invalid data duration')
      return null;
    }
    
    const payload = {
      wallets: validWallets,
      days,
      rf: 0.01,
      benchmark: 'ETH',
    };
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const response = await axios.post(`${apiUrl}asset_top_traders`, payload, {
      headers,
      signal
    });
    
    store.dispatch(setTopTraders({ data: response.data }));
    return response.data;
  } catch (e) {
    if (e.name === 'AbortError') return;
    store.dispatch(isLoading({ topTraders: false }));
    console.error('Error fetching top traders');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid trader data provided')
      } else {
        toast.error('Unable to fetch top traders. Please try again.')
      }
    } else {
      toast.error('Unable to fetch top traders. Please try again.')
    }
    return null;
  }
}








/////////////////////////////////
// GET PORTFOLIO VS BENCHMARK 
/////////////////////////////////

// POST / Get benchmark comparison
export async function getPortfolioBenchmarkComparison(
 accessToken: string,
 days = 7,
 rf = 0.01,
 benchmark = 'ETH'
) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    store.dispatch(isLoading({ portBench: true }));
    
    const wallets = store.getState().user.wallets;
    
    // Validate wallets from store
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('No wallets found in store')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    // Validate parameters
    if (typeof days !== 'number' || days <= 0) {
      console.error('Invalid days parameter')
      return null;
    }
    if (typeof rf !== 'number') {
      console.error('Invalid rf parameter')
      return null;
    }
    if (typeof benchmark !== 'string' || benchmark.trim().length === 0) {
      console.error('Invalid benchmark parameter')
      return null;
    }
    
    const payload = {
      wallets: validWallets,
      days,
      rf,
      benchmark: benchmark.trim(),
    };
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const data = await axios.post(`${apiUrl}port_vs_bench`, payload, { headers });
    store.dispatch(setPortBench(data?.data));
    return data?.data;
  } catch (e) {
    store.dispatch(isLoading({ portBench: false }));
    console.error('Error fetching portfolio benchmark comparison');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid benchmark comparison data provided')
      } else {
        toast.error('Unable to fetch benchmark comparison. Please try again.')
      }
    } else {
      toast.error('Unable to fetch benchmark comparison. Please try again.')
    }
    return null;
  }
}







/////////////////////////////////
// GET HISTORIC HOLDER COUNT 
/////////////////////////////////

// POST / https://api.astrotek.app/api/historic_holder_count
export async function getHistoricHolderCount(
 accessToken: string,
 benchmark = 'ETH'
) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    store.dispatch(isLoading({ historicHolder: true }));
    
    const wallets = store.getState().user.wallets;
    const dataDuration = store.getState().user.dataDuration?.id;
    
    // Validate wallets from store
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('No wallets found in store')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    // Validate parameters
    const days = dataDuration || 14;
    if (typeof days !== 'number' || days <= 0) {
      console.error('Invalid data duration')
      return null;
    }
    if (typeof benchmark !== 'string' || benchmark.trim().length === 0) {
      console.error('Invalid benchmark parameter')
      return null;
    }
    
    const payload = {
      wallets: validWallets,
      days,
      rf: 0.01,
      benchmark: benchmark.trim(),
    };
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const data = await axios.post(`${apiUrl}historic_holder_count`, payload, { headers });
    store.dispatch(setHistoricHolder(data?.data));
    return data?.data;
  } catch (e) {
    store.dispatch(isLoading({ historicHolder: false }));
    console.error('Error fetching historic holder count');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid historic holder data provided')
      } else {
        toast.error('Unable to fetch historic holder count. Please try again.')
      }
    } else {
      toast.error('Unable to fetch historic holder count. Please try again.')
    }
    return null;
  }
}








/////////////////////////////////
// GET ASSET CORRELATIONS 
/////////////////////////////////

// POST / https://api.astrotek.app/api/asset_correlations
export async function getAssetCorrelations(
 accessToken: string,
 benchmark = 'ETH'
) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    store.dispatch(isLoading({ assetCorrelations: true }));
    
    const wallets = store.getState().user.wallets;
    const dataDuration = store.getState().user.dataDuration?.id;
    
    // Validate wallets from store
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('No wallets found in store')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    // Validate parameters
    const days = dataDuration || 14;
    if (typeof days !== 'number' || days <= 0) {
      console.error('Invalid data duration')
      return null;
    }
    if (typeof benchmark !== 'string' || benchmark.trim().length === 0) {
      console.error('Invalid benchmark parameter')
      return null;
    }
    
    const payload = {
      wallets: validWallets,
      days,
      rf: 0.01,
      benchmark: benchmark.trim(),
    };
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const data = await axios.post(`${apiUrl}asset_correlations`, payload, { headers });
    store.dispatch(setAssetCorrelations(data?.data));
    return data?.data;
  } catch (e) {
    store.dispatch(isLoading({ assetCorrelations: false }));
    console.error('Error fetching asset correlations');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid correlation data provided')
      } else {
        toast.error('Unable to fetch asset correlations. Please try again.')
      }
    } else {
      toast.error('Unable to fetch asset correlations. Please try again.')
    }
    return null;
  }
}










/////////////////////////////////
// GET MAX DRAWDOWN 
/////////////////////////////////

// POST / https://api.astrotek.app/api/portfolio_drawdown_analysis
export async function getMaxDrawDown(
 accessToken: string,
 benchmark = 'ETH'
) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    store.dispatch(isLoading({ maxDrawDown: true }));
    
    const wallets = store.getState().user.wallets;
    const dataDuration = store.getState().user.dataDuration?.id;
    
    // Validate wallets from store
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('No wallets found in store')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    // Validate parameters
    const days = dataDuration || 14;
    if (typeof days !== 'number' || days <= 0) {
      console.error('Invalid data duration')
      return null;
    }
    if (typeof benchmark !== 'string' || benchmark.trim().length === 0) {
      console.error('Invalid benchmark parameter')
      return null;
    }
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const payload = {
      wallets: validWallets,
      days,
      rf: 0.01,
      benchmark: benchmark.trim(),
    };
    
    const { data } = await axios.post(`${apiUrl}portfolio_drawdown_analysis`, payload, { headers });
    
    // Parse if it's a string
    const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
    
    store.dispatch(setMaxDrawDown(parsedData));
    store.dispatch(isLoading({ maxDrawDown: false }));
    
    return parsedData;
  } catch (e) {
    store.dispatch(isLoading({ maxDrawDown: false }));
    console.error('Error fetching max drawdown data');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid drawdown data provided')
      } else {
        toast.error('Unable to fetch max drawdown data. Please try again.')
      }
    } else {
      toast.error('Unable to fetch max drawdown data. Please try again.')
    }
    return null;
  }
}









/////////////////////////////////
// GET PNL DATA 
/////////////////////////////////

// POST / Get PnL
export async function getPnLData(accessToken: string, signal?: AbortSignal) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    store.dispatch(isLoading({ pnl: true }));
    
    const wallets = store.getState().user.wallets;
    
    // Validate wallets from store
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('No wallets found in store')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    const payload = {
      wallets: validWallets,
    };
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const data = await axios.post(`${apiUrl}portfolio_pnl`, payload, { headers });
    
    if (data?.data) {
      store.dispatch(setPnLData(data.data));
    } else {
      console.warn('No PnL data in response');
      store.dispatch(setPnLData([]));
    }
    
    return data;
  } catch (e) {
    store.dispatch(isLoading({ pnl: false }));
    store.dispatch(setPnLData([]));
    console.error('Error fetching PnL data');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid PnL data provided')
      } else {
        toast.error('Unable to fetch PnL data. Please try again.')
      }
    } else {
      toast.error('Unable to fetch PnL data. Please try again.')
    }
    return null;
  }
}







/////////////////////////////////
// GET TOKEN PAIR DATA 
/////////////////////////////////

// POST / Get token pair data
// Abort signal in payload
export async function getTokenPairData(
 accessToken: string,
 selectedContract?: string, 
 chain?: string, 
 limit: number = 10,
 signal?: AbortSignal
) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    store.dispatch(isLoading({ pairData: true }));
    
    // Validate limit parameter
    if (typeof limit !== 'number' || limit <= 0) {
      console.error('Invalid limit parameter')
      return null;
    }
    
    // Validate chain if provided
    if (chain && (typeof chain !== 'string' || chain.trim().length === 0)) {
      console.error('Invalid chain parameter')
      return null;
    }
    
    // Validate and sanitize contract address if provided
    let tokenAddresses: string[] = [];
    if (selectedContract) {
      tokenAddresses = validateTokenAddresses(selectedContract);
    }
    
    const payload = {
      token_addresses: tokenAddresses,
      chain: chain?.trim(),
      limit
    };
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const data = await axios.post(`${apiUrl}token_pair_stats`, payload, { headers, signal });
    
    if (data?.data) {
      store.dispatch(setPairData(data.data));
    } else {
      store.dispatch(setPairData([]));
    }
    
    return data;
  } catch (e) {
    if (e.name === 'AbortError') return;
    store.dispatch(isLoading({ pairData: false }));
    store.dispatch(setPairData([]));
    console.error('Error fetching token pair data');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid token pair data provided')
      } else {
        toast.error('Unable to fetch token pair data. Please try again.')
      }
    } else {
      toast.error('Unable to fetch token pair data. Please try again.')
    }
    return null;
  }
}






/////////////////////////////////
// GET TOKEN OHLCV 
/////////////////////////////////


interface TokenOHLCVParams {
  access_token: string;
  token_address?: string;
  chain?: string;
  from_date?: string;
  to_date?: string;
  time_frame?: string;
 }
 
// POST / Get token OHLCV
export async function getTokenOHLCV({
 access_token,
 token_address,
 chain = 'eth',
 from_date,
 to_date, 
 time_frame = '1h'
}: TokenOHLCVParams) {
  checkRateLimit(access_token, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(access_token);
  const validChain = validateChains(chain)[0];
  
  try {
    // Validate token address if provided
    let validatedAddress: string | undefined;
    if (token_address) {
      const validated = validateTokenAddresses(token_address);
      validatedAddress = validated[0];
    }
    
    // Validate chain
    // if (typeof chain !== 'string' || chain.trim().length === 0) {
    //   console.error('Invalid chain parameter')
    //   return null;
    // }
    
    // Validate time_frame
    if (typeof time_frame !== 'string' || time_frame.trim().length === 0) {
      console.error('Invalid time_frame parameter')
      return null;
    }
    
    const payload = {
      token_address: validatedAddress,
      // chain: chain.trim(),
      chain: validChain,
      from_date,
      to_date,
      time_frame: time_frame.trim()
    };
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const data = await axios.post(`${apiUrl}token_ohlcv`, payload, { headers });
    
    if (!data?.data) {
      console.warn('No OHLCV data in response');
    }
    
    return data;
  } catch (e) {
    console.error('Error fetching token OHLCV data');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid OHLCV data provided')
      } else {
        toast.error('Unable to fetch token OHLCV data. Please try again.')
      }
    } else {
      toast.error('Unable to fetch token OHLCV data. Please try again.')
    }
    return null;
  }
}






/////////////////////////////////
// GET TRANSFERS 
/////////////////////////////////


// POST / Get transfers
export async function getTransfers(access_token: string, token_addresses: string[]) {
  try {
    checkRateLimit(access_token, 20, 60000); // 20 requests per minute per token
    const validToken = validateAccessToken(access_token);
    const validAddresses = validateTokenAddresses(token_addresses);
    
    const payload = {
      token_addresses: validAddresses
    };
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const { data } = await axios.post(`${apiUrl}fetch_transfers`, payload, { headers });
    
    // Validate response structure
    if (!data || typeof data !== 'object') {
      console.error('Invalid API response format')
      return null;
    }
    
    return data;
  } catch (e) {
    console.error('Failed to fetch transfers');
    
    // Sanitize error for user
    if (axios.isAxiosError(e)) {
      if (e.response?.status === 401) {
        console.error('Authentication failed')
      } else if (e.response?.status === 400) {
        toast.error('Invalid transfer data provided')
      } else {
        toast.error('Unable to fetch transfers. Please try again.')
      }
    } else {
      toast.error('Unable to fetch transfers. Please try again.')
    }
    return null;
  }
}





/////////////////////////////////
// GET STREAMS 
/////////////////////////////////

// https://api.astrotek.app/api/streams/<stream_id>/addresses
// Interface for API response
interface StreamResponse {
  error?: boolean;
  message?: string;
  data?: any;
}

export async function getStreamId(accessToken: string): Promise<string> {
  try {
    const streamId = await fetchEnvVar('STREAM_ID', accessToken);
    return streamId;
  } catch (error) {
    console.error('Error fetching stream ID');
    throw error;
  }
}


// POST / API function to add address to stream
export async function addAddressToStream(
 access_token: string,
 token_addresses: string[]
): Promise<StreamResponse> {

  checkRateLimit(access_token, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(access_token);
  const validAddresses = validateTokenAddresses(token_addresses);
  
  try {
    const normalizedAddresses = validAddresses.map(address => address.toLowerCase());
    
    const payload = {
      subscription_addresses: normalizedAddresses 
    };
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const HARDCODED_STREAM_ID = await getStreamId(validToken);
    const url = `${apiUrl}streams/${HARDCODED_STREAM_ID}/addresses`;
    
    const { data } = await axios.post(url, payload, { headers });
    return {
      error: false,
      data
    };
  } catch (error) {
    console.error('Error adding addresses to stream');
    
    // Sanitize error for user
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        return {
          error: true,
          message: 'Authentication failed',
          data: null
        };
      } else if (error.response?.status === 400) {
        return {
          error: true,
          message: 'Invalid address data provided',
          data: null
        };
      } else {
        return {
          error: true,
          message: 'Unable to add address to stream. Please try again.',
          data: null
        };
      }
    }
    
    return {
      error: true,
      message: 'An unexpected error occurred',
      data: null
    };
  }
}







/////////////////////////////////
// GET PORTFOLIO ASSET SEARCH 
/////////////////////////////////

// POST / TODO: update wallet to pull from state and not hardcoded
export async function getPortfolioAssetSearch(
 accessToken: string,
 wallets,
 benchmark = 'ETH',
 days = 4,
 strategy = 'Diversify',
 tailSize = 5
) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  try {
    // Validate wallets
    if (!Array.isArray(wallets) || wallets.length === 0) {
      console.error('Wallets array is required and cannot be empty')
      return null;
    }
    
    const validWallets = validateTokenAddresses(wallets);
    
    // Validate parameters
    if (typeof days !== 'number' || days <= 0) {
      console.error('Invalid days parameter')
      return null;
    }
    if (typeof benchmark !== 'string' || benchmark.trim().length === 0) {
      console.error('Invalid benchmark parameter')
      return null;
    }
    if (typeof strategy !== 'string' || strategy.trim().length === 0) {
      console.error('Invalid strategy parameter')
      return null;
    }
    if (typeof tailSize !== 'number' || tailSize <= 0) {
      console.error('Invalid tailSize parameter')
      return null;
    }
    
    const headers = {
      'Authorization': `Bearer ${validToken}`,
      'Content-Type': 'application/json'
    };
    
    const payload = {
      wallets: validWallets,
      days,
      rf: 0.01,
      benchmark: benchmark.trim(),
      optimization_strategy: strategy.trim(),
      tail_size: tailSize
    };
    
    const { data } = await axios.post(`${apiUrl}portfolio_asset_search`, payload, { headers });
    return data;
  } catch (error) {
    console.error('Portfolio asset search error');
    
    // Sanitize error for user
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        console.error('Authentication failed')
      } else if (error.response?.status === 400) {
        toast.error('Invalid portfolio search data provided')
      } else {
        toast.error('Unable to search portfolio assets. Please try again.')
      }
    } else {
      toast.error('Unable to search portfolio assets. Please try again.')
    }
    return null;
  }
}









/////////////////////////////////
// GET TOKEN SNIPER ANALYSIS
/////////////////////////////////

// POST / Get token snipers
export async function getTokenSniperAnalysis(
 accessToken: string,
 contractAddress: string,
 chain: string,
 blocksAfterCreation: number
) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  // Validate contract address
  const validAddress = validateTokenAddresses(contractAddress)[0];
  
  // Validate parameters
  if (typeof chain !== 'string' || chain.trim().length === 0) {
    console.error('Invalid chain parameter')
    return null;
  }
  if (typeof blocksAfterCreation !== 'number' || blocksAfterCreation <= 0) {
    console.error('Invalid blocksAfterCreation parameter')
    return null;
  }
  
  const payload = {
    contract_address: validAddress,
    chain: chain.trim(),
    blocks_after_creation: blocksAfterCreation
  };
  
  try {
    const { data } = await axios.post(`${apiUrl}snipers`, payload, {
      headers: {
        'Authorization': `Bearer ${validToken}`,
        'Content-Type': 'application/json'
      }
    });
    return data;
  } catch (error) {
    console.error('Token sniper analysis error');
    
    // Sanitize error for user
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        console.error('Authentication failed')
      } else if (error.response?.status === 400) {
        toast.error('Invalid sniper analysis data provided')
      } else {
        toast.error('Unable to fetch sniper analysis. Please try again.')
      }
    } else {
      toast.error('Unable to fetch sniper analysis. Please try again.')
    }
    return null;
  }
}







/////////////////////////////////
// GET DETAILED HISTORIC HOLDERS
/////////////////////////////////


// POST / Get detailed historical holders
export async function getDetailedHistoricHolders(
 accessToken: string,
 contractAddress: string,
 fromDate: string,
 chain: string = "eth",
 interval: string = "1d",
 signal?: AbortSignal
) {
  checkRateLimit(accessToken, 20, 60000); // 20 requests per minute per token
  const validToken = validateAccessToken(accessToken);
  
  // Validate contract address
  const validAddress = validateTokenAddresses(contractAddress)[0];
  
  // Validate parameters
  if (typeof fromDate !== 'string' || fromDate.trim().length === 0) {
    console.error('Invalid fromDate parameter')
    return null;
  }
  if (typeof chain !== 'string' || chain.trim().length === 0) {
    console.error('Invalid chain parameter')
    return null;
  }
  if (typeof interval !== 'string' || interval.trim().length === 0) {
    console.error('Invalid interval parameter')
    return null;
  }
  
  const payload = {
    contract_address: validAddress,
    from_date: fromDate.trim(),
    chain: chain.trim(),
    interval: interval.trim()
  };
  
  try {
    const { data } = await axios.post(`${apiUrl}detailed_historic_holders`, payload, {
      headers: {
        'Authorization': `Bearer ${validToken}`,
        'Content-Type': 'application/json'
      },
      signal
    });
    
    return data;
  } catch (error) {
    if (error.name === 'AbortError') return;
    console.error('Detailed historic holders error');
    
    // Sanitize error for user
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        console.error('Authentication failed')
      } else if (error.response?.status === 400) {
        toast.error('Invalid historic holder data provided')
      } else {
        toast.error('Unable to fetch detailed historic holders. Please try again.')
      }
    } else {
      toast.error('Unable to fetch detailed historic holders. Please try again.')
    }
    return null;
  }
}